import { useState } from "react";
import { toast } from "sonner";
import { Search, Facebook, Info, SlidersHorizontal, Hash, CreditCard } from "lucide-react";
import { Button, Input, Card } from "@/components/kit";
import CityCombobox from "@/components/CityCombobox";
import ScrapeResults from "@/components/ScrapeResults";
import ScrapeHistory from "@/components/ScrapeHistory";
import useScrape from "@/lib/useScrape";

export default function FacebookScraper() {
  const [keyword, setKeyword] = useState("");
  const [city, setCity] = useState("Jakarta");
  const [count, setCount] = useState(20);
  const [onlyPhone, setOnlyPhone] = useState(false);
  const { results, setResults, selected, loading, saving, run, toggle, save, selectedLeads, suggestions, progress } = useScrape("facebook");

  const onRun = () => {
    if (!keyword.trim()) { toast.error("Masukkan keyword (mis. nama bisnis / niche)"); return; }
    run({ keyword, city, count, only_phone: onlyPhone });
  };

  const onRerun = (h) => {
    setKeyword(h.keyword);
    setCity(h.city);
    run({ keyword: h.keyword, city: h.city, count, only_phone: onlyPhone });
  };

  return (
    <div className="space-y-5 animate-float-up">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-[#1877F2]/10 grid place-items-center shadow-sm">
            <Facebook className="w-5.5 h-5.5 text-[#1877F2]" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight">Scraper Facebook</h1>
            <p className="text-slate-400 mt-0.5 text-xs">Temukan halaman bisnis Facebook beserta kontaknya.</p>
          </div>
        </div>
      </div>

      {/* Info banner */}
      <Card className="p-3.5 bg-[#1877F2]/[0.03] border-[#1877F2]/15">
        <p className="text-xs text-[#1877F2] flex items-center gap-2">
          <Info className="w-3.5 h-3.5 shrink-0" />
          Cari halaman bisnis berdasarkan keyword & kota.
          Setiap hasil memakai <span className="font-bold bg-[#1877F2]/10 px-1.5 py-0.5 rounded">2 kredit</span> (data kontak lebih kaya).
        </p>
      </Card>

      {/* Form */}
      <div className="grid grid-cols-1 xl:grid-cols-[380px_1fr] gap-4 items-start">
        <div className="space-y-3 order-2 xl:order-1">
          {/* Search */}
          <Card className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider">
              <Search className="w-3.5 h-3.5" /> Pencarian
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1 block">Keyword / Niche</label>
              <div className="relative">
                <Search className="w-4 h-4 text-slate-300 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input data-testid="scrape-keyword" placeholder="coffee shop, fashion store, klinik..." value={keyword}
                  onChange={(e) => setKeyword(e.target.value)} className="pl-9 h-10 text-sm rounded-xl"
                  onKeyDown={(e) => e.key === "Enter" && onRun()} />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1 block">Kota / Lokasi</label>
              <button
                onClick={() => setCity("")}
                className={`text-[11px] font-semibold mb-1.5 px-3 py-1 rounded-lg border transition ${!city ? "bg-[#1877F2] text-white border-[#1877F2]" : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"}`}
              >
                🇮🇩 Semua Indonesia
              </button>
              {city && <CityCombobox value={city} onSelect={(c) => setCity(c.name || "")} />}
              {!city && <p className="text-[10px] text-[#1877F2] font-medium mt-1">Semua kota di Indonesia</p>}
            </div>
            <Button data-testid="scrape-run" onClick={onRun} loading={loading} size="lg"
              className="w-full h-11 rounded-xl bg-[#1877F2] hover:bg-[#1463cc] text-white font-semibold shadow-lg shadow-blue-500/20 transition-all duration-200">
              <Search className="w-4 h-4" /> Cari Halaman
            </Button>
          </Card>

          {/* Parameters */}
          <Card className="p-4 space-y-4">
            <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider">
              <SlidersHorizontal className="w-3.5 h-3.5" /> Parameter
            </div>
            <div>
              <label className="flex justify-between text-[11px] font-semibold text-slate-400 mb-1.5">
                <span className="flex items-center gap-1"><Hash className="w-3 h-3" /> JUMLAH HASIL</span>
                <span className="text-[#1877F2] font-bold">{count}</span>
              </label>
              <input type="range" min="5" max="100" step="5" value={count} onChange={(e) => setCount(Number(e.target.value))}
                className="w-full h-1.5 rounded-full" />
            </div>
            <label className="flex items-center gap-2.5 text-sm text-slate-600 cursor-pointer group">
              <input type="checkbox" checked={onlyPhone} onChange={(e) => setOnlyPhone(e.target.checked)} />
              <span className="group-hover:text-slate-800 transition text-[13px]">Hanya yang punya nomor telepon</span>
            </label>
          </Card>

          <div className="flex items-center justify-center gap-2 text-[11px] text-slate-400 py-1">
            <CreditCard className="w-3 h-3" />
            Estimasi: <span className="font-bold text-[#1877F2]">{count * 2}</span> kredit
          </div>

          <ScrapeHistory source="facebook" onRerun={onRerun} />
        </div>

        {/* Results */}
        <div className="order-1 xl:order-2">
          <ScrapeResults source="facebook" results={results} setResults={setResults} loading={loading} selected={selected} toggle={toggle}
            selectedLeads={selectedLeads} save={save} saving={saving} progress={progress} suggestions={suggestions}
            emptyHint="Hasil pencarian halaman Facebook akan tampil di sini." />
        </div>
      </div>
    </div>
  );
}
